import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Settings, Save, AlertCircle, CheckCircle, CreditCard, Wallet, Smartphone, Truck } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentGatewaySettings = () => {
  const [settings, setSettings] = useState({
    razorpay_enabled: false,
    razorpay_key: '',
    razorpay_secret: '',
    paytm_enabled: false,
    paytm_key: '',
    paytm_secret: '',
    instamojo_enabled: false,
    instamojo_api_key: '',
    instamojo_auth_token: '',
    instamojo_payment_link: '',
    instamojo_sandbox: true,
    cod_enabled: true,
    skip_payment_page: false,
    submit_button_text: 'Proceed to Payment'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payment/settings`);
      setSettings(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load payment settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/payment/settings`, settings);
      toast({
        title: "Success",
        description: "Payment settings updated successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update payment settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const getEnabledCount = () => {
    let count = 0;
    if (settings.razorpay_enabled) count++;
    if (settings.paytm_enabled) count++;
    if (settings.instamojo_enabled) count++;
    if (settings.cod_enabled) count++;
    return count;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const paymentGateways = [
    {
      id: 'razorpay',
      name: 'Razorpay',
      icon: CreditCard,
      color: 'blue',
      hasKeys: true,
      keyLabel: 'API Key',
      secretLabel: 'API Secret'
    },
    {
      id: 'paytm',
      name: 'Paytm',
      icon: Wallet,
      color: 'cyan',
      hasKeys: true,
      keyLabel: 'Merchant Key',
      secretLabel: 'Merchant Secret'
    },
    {
      id: 'instamojo',
      name: 'Instamojo',
      icon: Smartphone,
      color: 'purple',
      hasKeys: false,  // Custom handling in the instamojo-specific section
      linkLabel: 'Static Payment Link (Optional)'
    },
    {
      id: 'cod',
      name: 'Cash on Delivery',
      icon: Truck,
      color: 'orange',
      hasKeys: false
    }
  ];

  return (
    <div className="space-y-6">
      {/* Skip Payment Page Option */}
      <Card className={settings.skip_payment_page ? "border-orange-300 bg-orange-50" : "border-slate-200"}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${settings.skip_payment_page ? 'bg-orange-100' : 'bg-slate-100'}`}>
                <Settings className={`w-5 h-5 ${settings.skip_payment_page ? 'text-orange-600' : 'text-slate-600'}`} />
              </div>
              <div>
                <CardTitle className="text-lg">Skip Payment Selection Page</CardTitle>
                <CardDescription>
                  Bypass the payment method selection step
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={settings.skip_payment_page || false}
              onCheckedChange={(checked) => setSettings({ ...settings, skip_payment_page: checked })}
              data-testid="skip-payment-page-toggle"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className={`p-4 rounded-lg ${settings.skip_payment_page ? 'bg-orange-100' : 'bg-slate-100'}`}>
            {settings.skip_payment_page ? (
              <div className="space-y-2">
                <p className="font-medium text-orange-900">⚡ Direct to Results Mode</p>
                <p className="text-sm text-orange-700">
                  Users will go directly from the prediction form to the results page. 
                  The paywall on the results page will handle payment collection.
                </p>
                <p className="text-xs text-orange-600 mt-2">
                  Flow: Form → Results (with paywall)
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="font-medium text-slate-900">📝 Standard Payment Flow</p>
                <p className="text-sm text-slate-600">
                  Users will see the payment method selection page before viewing results.
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Flow: Form → Payment Selection → Results
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Submit Button Text Customization */}
      <Card className="border-blue-200">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100">
              <CreditCard className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Submit Button Text</CardTitle>
              <CardDescription>
                Customize the text shown on the form submit button
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="submit-button-text">Button Label</Label>
              <Input
                id="submit-button-text"
                value={settings.submit_button_text || 'Proceed to Payment'}
                onChange={(e) => setSettings({ ...settings, submit_button_text: e.target.value })}
                placeholder="Proceed to Payment"
                className="mt-1"
                data-testid="submit-button-text-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                This text will appear on the main prediction form submit button
              </p>
            </div>
            {/* Preview */}
            <div className="p-4 bg-slate-50 rounded-lg border">
              <p className="text-xs text-slate-500 mb-2">Preview:</p>
              <Button className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600">
                {settings.submit_button_text || 'Proceed to Payment'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Payment Gateway Settings</CardTitle>
              <CardDescription>Configure payment methods and credentials</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-lg px-3 py-1">
                {getEnabledCount()} Active
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {paymentGateways.map((gateway) => {
              const Icon = gateway.icon;
              const isEnabled = settings[`${gateway.id}_enabled`];
              
              return (
                <Card key={gateway.id} className={`border-2 ${isEnabled ? 'border-green-200 bg-green-50' : 'border-slate-200'}`}>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Gateway Header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${gateway.color}-100`}>
                            <Icon className={`w-6 h-6 text-${gateway.color}-600`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{gateway.name}</h3>
                            <p className="text-sm text-slate-600">
                              {isEnabled ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {isEnabled ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-slate-400" />
                          )}
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={(checked) => setSettings({
                              ...settings,
                              [`${gateway.id}_enabled`]: checked
                            })}
                          />
                        </div>
                      </div>

                      {/* Gateway Configuration */}
                      {isEnabled && (
                        <div className="space-y-3 pl-15 border-l-2 border-slate-200 ml-6 pt-2">
                          {gateway.hasKeys && (
                            <>
                              <div>
                                <Label>{gateway.keyLabel}</Label>
                                <Input
                                  type="text"
                                  placeholder={`Enter ${gateway.keyLabel}`}
                                  value={settings[`${gateway.id}_key`] || ''}
                                  onChange={(e) => setSettings({
                                    ...settings,
                                    [`${gateway.id}_key`]: e.target.value
                                  })}
                                />
                              </div>
                              <div>
                                <Label>{gateway.secretLabel}</Label>
                                <Input
                                  type="password"
                                  placeholder={`Enter ${gateway.secretLabel}`}
                                  value={settings[`${gateway.id}_secret`] || ''}
                                  onChange={(e) => setSettings({
                                    ...settings,
                                    [`${gateway.id}_secret`]: e.target.value
                                  })}
                                />
                              </div>
                            </>
                          )}
                          
                          {gateway.id === 'instamojo' && (
                            <div className="space-y-4">
                              <div>
                                <Label>API Key</Label>
                                <Input
                                  type="text"
                                  placeholder="Enter Instamojo API Key"
                                  value={settings.instamojo_api_key || ''}
                                  onChange={(e) => setSettings({
                                    ...settings,
                                    instamojo_api_key: e.target.value
                                  })}
                                />
                              </div>
                              <div>
                                <Label>Auth Token</Label>
                                <Input
                                  type="password"
                                  placeholder="Enter Instamojo Auth Token"
                                  value={settings.instamojo_auth_token || ''}
                                  onChange={(e) => setSettings({
                                    ...settings,
                                    instamojo_auth_token: e.target.value
                                  })}
                                />
                              </div>
                              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                                <input
                                  type="checkbox"
                                  id="instamojo_sandbox"
                                  checked={settings.instamojo_sandbox || false}
                                  onChange={(e) => setSettings({
                                    ...settings,
                                    instamojo_sandbox: e.target.checked
                                  })}
                                  className="w-4 h-4"
                                />
                                <label htmlFor="instamojo_sandbox" className="text-sm">
                                  <span className="font-medium text-amber-800">Sandbox/Test Mode</span>
                                  <p className="text-xs text-amber-600">Use test.instamojo.com for testing (uncheck for live payments)</p>
                                </label>
                              </div>
                              <div className="border-t pt-4 mt-4">
                                <Label>{gateway.linkLabel}</Label>
                                <Input
                                  type="url"
                                  placeholder="https://www.instamojo.com/@username/..."
                                  value={settings.instamojo_payment_link || ''}
                                  onChange={(e) => setSettings({
                                    ...settings,
                                    instamojo_payment_link: e.target.value
                                  })}
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                  <strong>Fallback:</strong> If API keys are not set, users will be redirected to this static payment link
                                </p>
                              </div>
                              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                                <p className="text-xs text-purple-800">
                                  <strong>Setup Instructions:</strong><br/>
                                  1. Go to Instamojo Dashboard → API & Plugins → Create New Credentials<br/>
                                  2. Copy API Key and Auth Token<br/>
                                  3. For webhook, add: <code className="bg-purple-100 px-1 rounded">{process.env.REACT_APP_BACKEND_URL}/api/instamojo/webhook</code>
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {gateway.id === 'cod' && (
                            <p className="text-sm text-slate-600">
                              Cash on Delivery allows users to pay when they receive physical reports
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="gap-2 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Payment Settings'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Information Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900 mb-1">Payment Gateway Information</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Enable at least one payment method for users to complete transactions</li>
                <li>• API keys are securely stored and never exposed to frontend</li>
                <li>• Instamojo payment links redirect users to complete payment on Instamojo</li>
                <li>• COD option allows users to pay when receiving physical reports</li>
                <li>• Changes take effect immediately after saving</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentGatewaySettings;
